import React, { useContext, useEffect } from 'react';
import { graphql } from 'react-relay';
import { differenceInDays } from 'date-fns';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { ONBOARDING_PERSONALIZED_TEMPLATES_V2 } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import {
	ONBOARDING_EXPERIENCES,
	PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { HomeOnboardingActionCards } from './HomeOnboardingActionCards.tsx';
import { PTV2StoreContainer, usePTV2State } from './usePTV2Store.tsx';
import {
	type BLUEPRINT_NAMES,
	BLUEPRINT_NAMES_SET,
	mapBlueprintModuleKeyToTemplate,
	type TemplateInfoType,
} from './mappings/blueprintNameMapping.ts';
import { subAnchorToTemplatesMapping } from './mappings/subAnchorToTemplatesMapping.ts';
import { teamToTemplatesMapping } from './mappings/teamToTemplatesMapping.ts';
import type { WHITEBOARD_TEMPLATE_IDS } from './mappings/whiteboardMappings.ts';
import { useGetTeamAndSubanchor } from './hooks/useGetTeamAndSubanchor.tsx';
import { PTV2TemplateList } from './PTV2TemplateList.tsx';
import type {
	PersonalizedTemplatesV2AllTemplatesQuery$data,
	PersonalizedTemplatesV2AllTemplatesQuery$variables,
} from './__generated__/PersonalizedTemplatesV2AllTemplatesQuery.graphql.ts';
import { DEFAULT_POPULAR_TEMPLATES } from './mappings/defaultPopularTemplates.ts';
import { usePTV2TemplateUsage } from './hooks/usePTV2TemplateUsage.tsx';
import { PTV2WhiteboardTemplateCard } from './PTV2WhiteboardTemplateCard.tsx';
import { PTV2BlueprintTemplateCard } from './PTV2BlueprintTemplateCard.tsx';

const PersonalizedTemplatesV2AllTemplatesQuery = graphql`
	query PersonalizedTemplatesV2AllTemplatesQuery {
		allTemplates(usePersonalSpace: true) {
			nodes {
				templateId
				contentBlueprintId
				blueprintModuleCompleteKey
				itemModuleCompleteKey
				name
			}
		}
	}
`;

type PersonalizedTemplatesV2Props = {
	blueprintModuleKeyToTemplate: Record<string, TemplateInfoType>;
	serializedTemplateUsage: string | undefined;
};

export const PersonalizedTemplatesV2 = ({
	blueprintModuleKeyToTemplate,
	serializedTemplateUsage,
}: PersonalizedTemplatesV2Props) => {
	const { subAnchor, teamType, personalizationHeuristic, onboardingPeriodDay, isXflow } =
		usePTV2State();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// If the subanchor templates are not available, use the team templates
	const neededTemplates =
		subAnchorToTemplatesMapping[subAnchor.toLocaleUpperCase()] ||
		teamToTemplatesMapping[teamType.toLocaleUpperCase()] ||
		DEFAULT_POPULAR_TEMPLATES;

	const { deserializedTemplateUsage, incrementTemplateUsage } = usePTV2TemplateUsage(
		serializedTemplateUsage,
		neededTemplates,
	);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'personalizedTemplatesV2',
				source: 'cc-onboarding-personalized-templates-v2',
				attributes: {
					personalizationHeuristic,
					onboardingPeriodDay,
					isXflow,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, subAnchor, personalizationHeuristic, onboardingPeriodDay, isXflow]);

	const cards: React.JSX.Element[] = neededTemplates.reduce(
		(current: React.JSX.Element[], templateName: BLUEPRINT_NAMES | WHITEBOARD_TEMPLATE_IDS) => {
			if (BLUEPRINT_NAMES_SET.has(templateName as BLUEPRINT_NAMES)) {
				return [
					...current,
					<PTV2BlueprintTemplateCard
						key={templateName}
						usage={deserializedTemplateUsage[templateName as BLUEPRINT_NAMES] || 0}
						onClick={() => {
							incrementTemplateUsage(templateName);
						}}
						templateName={templateName as BLUEPRINT_NAMES}
						blueprintModuleKeyToTemplate={blueprintModuleKeyToTemplate}
					/>,
				];
			} else {
				return [
					...current,
					<PTV2WhiteboardTemplateCard
						key={templateName}
						usage={deserializedTemplateUsage[templateName as WHITEBOARD_TEMPLATE_IDS] || 0}
						onClick={() => {
							incrementTemplateUsage(templateName);
						}}
						templateName={templateName as WHITEBOARD_TEMPLATE_IDS}
					/>,
				];
			}
		},
		[],
	);

	return <PTV2TemplateList cards={cards} />;
};

export const PersonalizedTemplatesV2Store = ({ isXflow }: { isXflow: boolean }) => {
	const {
		teamType,
		personalSpaceKey,
		subAnchor,
		personalSpaceKeyAndTeamLoading,
		subAnchorLoading,
	} = useGetTeamAndSubanchor({ skip: false });

	const { setOnboardingState } = useSetOnboardingState();

	const { data: allTemplatesData, loading: allTemplatesLoading } = useQueryInterop<
		PersonalizedTemplatesV2AllTemplatesQuery$data,
		PersonalizedTemplatesV2AllTemplatesQuery$variables
	>(PersonalizedTemplatesV2AllTemplatesQuery);

	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState([
		ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
		...Object.values(PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS),
	]);
	const { isTemplateListDismissed, templateUsageValues, takeoverSeenAt } =
		deserializeState(onboardingStateData);

	useEffect(() => {
		if (!onboardingStateLoading && !Boolean(takeoverSeenAt)) {
			setOnboardingState({
				key: PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS.takeoverSeenAt,
				value: new Date().toISOString(),
			});
		}
	}, [onboardingStateLoading, takeoverSeenAt, setOnboardingState]);

	const onboardingPeriodDay = takeoverSeenAt
		? differenceInDays(new Date(), new Date(takeoverSeenAt))
		: 0;

	if (
		personalSpaceKeyAndTeamLoading ||
		subAnchorLoading ||
		allTemplatesLoading ||
		onboardingStateLoading
	) {
		return null;
	}

	const blueprintModuleKeyToTemplate = mapBlueprintModuleKeyToTemplate(allTemplatesData);

	const personalizationHeuristic = subAnchorToTemplatesMapping[subAnchor.toLocaleUpperCase()]
		? 'subAnchor'
		: teamToTemplatesMapping[teamType.toLocaleUpperCase()]
			? 'teamType'
			: 'popular';

	return (
		<PTV2StoreContainer
			teamType={teamType}
			personalSpaceKey={personalSpaceKey}
			subAnchor={subAnchor}
			isTemplateListDismissed={isTemplateListDismissed}
			personalizationHeuristic={personalizationHeuristic}
			onboardingPeriodDay={onboardingPeriodDay}
			isXflow={isXflow}
		>
			<ExperienceSuccess
				name={ONBOARDING_PERSONALIZED_TEMPLATES_V2}
				attributes={{ personalizationHeuristic }}
			/>
			<HomeOnboardingActionCards onboardingStateData={onboardingStateData} />
			<PersonalizedTemplatesV2
				blueprintModuleKeyToTemplate={blueprintModuleKeyToTemplate}
				serializedTemplateUsage={templateUsageValues}
			/>
		</PTV2StoreContainer>
	);
};

export const PersonalizedTemplatesV2ErrorBoundary = ({ isXflow }: { isXflow: boolean }) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	return (
		<TransparentErrorBoundary
			attribution={Attribution.FLYWHEEL_ACTIVATION}
			onError={(error) => {
				experienceTracker.stopOnError({
					name: ONBOARDING_PERSONALIZED_TEMPLATES_V2,
					error,
				});
			}}
		>
			<PersonalizedTemplatesV2Store isXflow={isXflow} />
		</TransparentErrorBoundary>
	);
};
